import axios, { AxiosError, AxiosResponse, getAdapter, InternalAxiosRequestConfig } from "axios";
import { isPublicRoute } from "../routes/publicRoutes";
import { authService } from "../services";
import { reconnectSocket } from "../socket";
import { generateIdempotencyKey, IDEMPOTENCY_HEADER } from "./idempotency";

// Cache for in-flight requests by idempotency key
// Stores the promise of the original request so duplicates can wait for it
const inFlightRequests = new Map<string, Promise<AxiosResponse>>();

const instance = axios.create({
  baseURL: process.env.REACT_APP_API_ENDPOINT || "http://localhost:8080",
  timeout: 30 * 60 * 1000, // 30 minutes for large video uploads
  withCredentials: true, // Enable sending cookies with requests
});

// Add a request interceptor to dynamically update the Authorization header
instance.interceptors.request.use(
  (config) => {
    const token = authService.getToken("token");
    const deviceId = authService.getDeviceId();
    // Get the latest token
    if (token) {
      config.headers.Authorization = token; // Set the token in the Authorization header
    } else {
      const currentPath = window.location.pathname;
      if (!isPublicRoute(currentPath)) {
        authService.clearTokens();
        window.location.href = "/auth/signIn";
      }
    }
    // Add deviceId header if available
    if (deviceId) {
      config.headers["X-Device-Id"] = deviceId;
    }

    // Add idempotency key for POST/PUT/PATCH requests (mutating operations)
    // Only add if not already present (allows manual override)
    if (["post", "put", "patch"].includes(config.method?.toLowerCase() || "")) {
      config.headers[IDEMPOTENCY_HEADER] = generateIdempotencyKey();
    }

    return config;
  },
  (error) => {
    // Handle request error here
    return Promise.reject(error);
  }
);

// Wrap the request adapter to track in-flight requests by idempotency key
const originalAdapter = getAdapter(axios.defaults.adapter!);
instance.defaults.adapter = async (config: InternalAxiosRequestConfig) => {
  const idempotencyKey = config.headers?.[IDEMPOTENCY_HEADER] as string;

  if (idempotencyKey) {
    // Check if there's already an in-flight request with this key
    const existingRequest = inFlightRequests.get(idempotencyKey);
    if (existingRequest) {
      // Wait for the original request to complete and return its response
      console.log(`Duplicate request detected for key ${idempotencyKey}, waiting for original...`);
      return existingRequest;
    }

    // Create the request promise and store it
    const requestPromise = (async () => {
      try {
        const response = await originalAdapter(config);
        return response;
      } finally {
        // Clean up after request completes (success or failure)
        // Keep for 10 seconds to handle late duplicates
        setTimeout(() => {
          inFlightRequests.delete(idempotencyKey);
        }, 10000);
      }
    })();

    inFlightRequests.set(idempotencyKey, requestPromise);
    return requestPromise;
  }

  return originalAdapter(config);
};

let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;
let failedQueue: any = [];
const processQueue = (error: any, token = null) => {
  failedQueue.forEach((prom: any) => (error ? prom.reject(error) : prom.resolve(token)));
  failedQueue = [];
};

instance.interceptors.response.use(
  (res) => res,
  async (err) => {
    const originalReq = err.config;

    // Handle 409 Conflict for duplicate requests
    if (err.response?.status === 409) {
      const errorType = err.response?.data?.error;
      if (errorType === "DUPLICATE_REQUEST_PROCESSING" || errorType === "DUPLICATE_REQUEST_COMPLETED") {
        const idempotencyKey = originalReq.headers?.[IDEMPOTENCY_HEADER];
        if (idempotencyKey) {
          const originalRequest = inFlightRequests.get(idempotencyKey);
          if (originalRequest) {
            console.log(`Returning cached response for key ${idempotencyKey}`);
            return originalRequest;
          }
        }

        // If we can't find the original and it's already completed, treat as success
        if (errorType === "DUPLICATE_REQUEST_COMPLETED") {
          console.log("Request already completed, treating as success");
          return { data: { success: true, message: "Request already processed" }, status: 200 };
        }
      }
    }

    try {
      const isDisconnected = localStorage.getItem("isDisconnected") === "true";

      const isSignupRestricted =
        err.response?.data?.message?.includes("signup-related routes") || err.response?.data?.message?.includes("complete your registration");

      if (err.response?.status === 403 && !originalReq._retry && !isDisconnected) {
        if (isSignupRestricted) {
          console.log("⚠️ 403 due to signup restriction - skipping token refresh");
          return Promise.reject(err);
        }

        // if (err.response.data.message === "Device has been disconnected" ) {
        //   isAuthorizing = false;
        //   processQueue(err, null);
        //   isRefreshing = false;
        //   refreshPromise = null;

        //   // authService.signOut();
        //   authService.clearTokens();
        //   window.location.href = "/auth/signIn";

        //   return Promise.reject(err);
        // }

        if (isRefreshing && refreshPromise) {
          // Wait for the ongoing refresh to complete
          try {
            const token = await refreshPromise;
            originalReq.headers["Authorization"] = token;
            originalReq._retry = true;
            return instance(originalReq);
          } catch (refreshError) {
            return Promise.reject(refreshError);
          }
        }

        // Set flag and create promise immediately to prevent race condition
        isRefreshing = true;
        originalReq._retry = true;

        refreshPromise = (async () => {
          try {
            const refreshToken = authService.getRefreshToken();
            const deviceId = authService.getDeviceId();

            const resp = await axios.post(
              "/auth/refresh",
              { refreshToken, deviceId },
              { baseURL: process.env.REACT_APP_API_ENDPOINT, withCredentials: true }
            );
            const accessToken = resp.data.data.token;
            authService.setToken(accessToken, false);
            authService.setRefreshToken(resp.data.data.refreshToken);

            // Reconnect socket with new token
            reconnectSocket(accessToken);

            instance.defaults.headers.common["Authorization"] = accessToken;
            processQueue(null, accessToken);
            return accessToken;
          } catch (e: any) {
            processQueue(e, null);
            if (e.response?.status === 401) {
              localStorage.setItem("isDisconnected", "true");
              await authService.signOut();
              window.location.href = "/auth/signIn";
            }
            throw e;
          } finally {
            isRefreshing = false;
            refreshPromise = null;
          }
        })();

        try {
          const token = await refreshPromise;
          originalReq.headers["Authorization"] = token;
          return instance(originalReq);
        } catch (refreshError) {
          return Promise.reject(refreshError);
        }
      } else if (err.response?.status === 401 && !isDisconnected) {
        console.log("🔴 401 Unauthorized - redirecting to login", err.response?.data);
        localStorage.setItem("isDisconnected", "true");
        await authService.signOut();
        authService.clearTokens();
        window.location.href = "/auth/signIn";
      }
    } catch (e: AxiosError | any) {
      return Promise.reject(e);
    }

    return Promise.reject(err);
  }
);

export default instance;
